import type { BOM, Item, WorkOrder } from '../../services/types';

export function formatSalesOrderRef(soId?: number | null) {
    return soId ? `SO-${soId}` : '';
}

export function formatEstimateRef(order: Pick<WorkOrder, 'estimate_id' | 'estimate_ref'>) {
    if (order.estimate_ref) return order.estimate_ref;
    return order.estimate_id ? `EST-${order.estimate_id}` : '';
}

export function getWorkOrderSourceLabel(order: Pick<WorkOrder, 'source_type' | 'so_id' | 'estimate_id' | 'bom_id'>) {
    if (order.so_id || order.source_type === 'SO') return 'SO';
    if (order.bom_id || order.source_type === 'BOM') return 'BOM';
    if (order.estimate_id || order.source_type === 'ESTIMATE') return 'ESTIMATE';
    return 'UNKNOWN';
}

export function getWorkOrderPrimaryReference(order: Pick<WorkOrder, 'so_id' | 'estimate_id' | 'estimate_ref' | 'bom_id' | 'source_type'>) {
    const soRef = formatSalesOrderRef(order.so_id);
    if (soRef) {
        return { label: 'Sales Order', value: soRef };
    }

    const estimateRef = formatEstimateRef(order);
    if (estimateRef) {
        return { label: 'Estimate / Quote Ref', value: estimateRef };
    }

    if (order.bom_id) {
        return { label: 'BOM', value: `BOM-${order.bom_id}` };
    }

    return { label: 'Reference', value: 'Custom / BOM Work Order' };
}

export function getWorkOrderSearchText(order: WorkOrder) {
    return [
        order.wo_id,
        order.wo_number,
        order.status,
        order.remarks,
        ...getWorkOrderDescriptionLines(order),
    ].filter(Boolean).join(' ').toLowerCase();
}

export function getWorkOrderItemLabel(order: WorkOrder, boms: BOM[], items: Item[]) {
    if (order.bom_id) {
        const bom = boms.find((candidate) => candidate.id === order.bom_id);
        if (bom) {
            return items.find((item) => item.id === bom.finished_good_id)?.name || `Item ${bom.finished_good_id}`;
        }
        return 'Unknown BOM';
    }

    const primaryRef = getWorkOrderPrimaryReference(order);
    return `${primaryRef.label}: ${primaryRef.value}`;
}

export function getOperatorWorkOrderLabel(order: Pick<WorkOrder, 'wo_id' | 'wo_number'>) {
    return order.wo_number || `WO-${order.wo_id}`;
}

export function getWorkOrderDescriptionLines(order: Pick<WorkOrder, 'items' | 'remarks'>) {
    const mainLines = (order.items || [])
        .filter((item) => {
            const role = String(item.line_role || 'LINE').toUpperCase();
            return !item.parent_wo_item_id && role !== 'CHILD';
        })
        .map((item) => String(item.description || '').trim())
        .filter(Boolean);

    if (mainLines.length > 0) return mainLines;

    const childLines = (order.items || [])
        .map((item) => String(item.description || '').trim())
        .filter(Boolean);
    if (childLines.length > 0) return childLines;

    const remarks = String(order.remarks || '').trim();
    return remarks ? [remarks] : [];
}

export function getWorkOrderDescriptionSummary(order: Pick<WorkOrder, 'items' | 'remarks'>) {
    const lines = getWorkOrderDescriptionLines(order);
    if (lines.length === 0) return 'No project description';
    if (lines.length === 1) return lines[0];
    return `${lines[0]} + ${lines.length - 1} more`;
}
